import isServerSideRendering from './isServerSideRendering';

/**
 * Attempts to retrieve a value by name from `window.localStorage`.
 * Is safe (but ineffectual) to run on the server.
 *
 * @example
 * import { loadItemFromLocalStorage } from '@carvana/futilities/localStorage';
 * loadItemFromLocalStorage('token')
 * // () => 'e2xvcmVtOiAnaXBzdW0nfQ=='
 *
 * @kind function
 * @name loadItemFromLocalStorage
 * @sig String -> *
 * @param {String} name The name of a (possible) value in localStorage
 * @returns {*} Either the value pulled from localStorage or `undefined`
 */
export function loadItemFromLocalStorage(name: string) {
  try {
    if (isServerSideRendering()) return undefined;
    const serialziedState = window.localStorage.getItem(name);
    return serialziedState ? JSON.parse(serialziedState) : undefined;
  } catch (err) {
    return undefined;
  }
}

/**
 * Commits a given value to `window.localStorage` with the specified key name.
 *
 * @example
 * import { saveItemToLocalStorage } from '@carvana/futilities/localStorage';
 * saveItemToLocalStorage('token', 'e2xvcmVtOiAnaXBzdW0nfQ==')
 * // () => true
 *
 * @kind function
 * @name saveItemToLocalStorage
 * @sig String -> * -> Boolean
 * @param {String} name The key name to give to the value in localStorage
 * @param {*} val A value to commit to localStorage
 * @returns {Boolean} Whether or not the provided value was successfully committed to localStorage
 */
export function saveItemToLocalStorage(name: string, val: any) {
  try {
    if (isServerSideRendering()) return false;
    window.localStorage.setItem(name, JSON.stringify(val));
    return true;
  } catch (err) {
    // ignoring write errors for the time being
    return false;
  }
}

/**
 * Deletes a given value from `window.localStorage` with the specified key name.
 *
 * @example
 * import { deleteItemFromLocalStorage } from '@carvana/futilities/localStorage';
 * deleteItemFromLocalStorage('token')
 * // () => true
 *
 * @kind function
 * @name deleteItemFromLocalStorage
 * @sig String -> * -> Boolean
 * @param {String} name The key name in localStorage
 * @returns {Boolean} Whether or not the provided value was successfully deleted from localStorage
 */
export function deleteItemFromLocalStorage(name: string) {
  try {
    if (isServerSideRendering()) return false;
    window.localStorage.removeItem(name);
    return true;
  } catch (err) {
    return false;
  }
}
